/**
 * Mount-picker dialog — opens from catalog …tags controls (shared mount list
 * applies to RULES or REFERENCE depending on lane subscription above).
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import MountTargetPicker from './MountTargetPicker.jsx';

/** @typedef {{ fileName?: string, sizeBytes?: number, contentHash?: string }} AssetLite */

/** @param {object[]} mounts */
export function mountsToTargets(mounts) {
  if (!Array.isArray(mounts)) return [];
  return mounts.map((m) => {
    const t = m?.target ?? m;
    return t && typeof t === 'object' ? { ...t } : t;
  }).filter(Boolean);
}

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   asset?: (AssetLite & { assetId?: string, mounts?: object[] }) | null,
 *   onSave: (nextMounts: object[]) => Promise<void> | void,
 *   networkId?: string,
 *   agentNames?: string[],
 *   poolIds?: string[],
 *   includeOrg?: boolean,
 *   mountSuggestMode?: string,
 * }} props
 */
export default function TagAssetDialog({
  open,
  onClose,
  asset,
  onSave,
  networkId,
  agentNames,
  poolIds,
  includeOrg = false,
  mountSuggestMode,
  // Full mount set across all scopes/lanes for the pyramid coverage check
  // (``mounts`` here is the lane/network-filtered editable subset). Optional.
  coverageMounts = null,
}) {
  const [mounts, setMounts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setMounts(mountsToTargets(asset?.mounts));
    setSubmitting(false);
    setSubmitError(null);
  }, [open, asset]);

  const onSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSave?.(mounts);
      onClose?.();
    } catch (e) {
      setSubmitError(e?.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape' && !submitting) onClose?.();
  };

  const fileName = asset?.fileName || 'Asset';

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          key="tag-asset-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-40 flex items-start justify-center bg-black/40 p-4 sm:items-center"
          onClick={onClose}
          onKeyDown={onKeyDown}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="relative w-full max-w-md rounded-xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Mounts and tags"
          >
            <header className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <h2 className="text-sm font-semibold text-gray-900">Mounts & tags</h2>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                aria-label="Close"
                className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </header>

            <div className="space-y-4 px-4 py-3">
              <section>
                <div className="text-xs font-medium text-gray-800">{fileName}</div>
                {asset?.contentHash ? (
                  <div className="mt-0.5 text-[11px] text-gray-500 tabular-nums">
                    {typeof asset.sizeBytes === 'number' ? `${formatBytes(asset.sizeBytes)} · ` : ''}
                    {shortHash(asset.contentHash)}
                  </div>
                ) : null}
              </section>

              <section>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Mount points
                </div>
                <MountTargetPicker
                  value={mounts}
                  onChange={setMounts}
                  networkId={networkId}
                  agentNames={agentNames}
                  poolIds={poolIds}
                  includeOrg={includeOrg}
                  mountSuggestMode={mountSuggestMode}
                  disabled={submitting}
                  // Pyramid coverage is checked against the asset's FULL mount set
                  // (all scopes/lanes) — not just the lane/network-filtered ``mounts``
                  // being edited — so a target already covered by a higher tag (e.g.
                  // @Tenant) is frozen upfront. ``coverageMounts`` is the unfiltered list.
                  coverageMounts={coverageMounts || mounts}
                />
                <p className="mt-1 text-[11px] text-gray-500">
                  Current mount targets (tags). Remove a chip to detach; use Add mount to tag more
                  networks/agents. Save applies changes.
                </p>
              </section>

              {submitError ? (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700">
                  {submitError}
                </div>
              ) : null}
            </div>

            <footer className="flex items-center justify-end gap-2 border-t border-gray-100 px-4 py-3">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-2.5 py-1 text-xs font-medium rounded-md text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onSubmit}
                disabled={submitting}
                className="px-2.5 py-1 text-xs font-medium rounded-md bg-blue-600 text-white transition-colors hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
              >
                {submitting ? 'Saving…' : 'Save'}
              </button>
            </footer>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

function formatBytes(n) {
  if (!Number.isFinite(n)) return `${n} B`;
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function shortHash(hash) {
  if (!hash) return '';
  const colon = hash.indexOf(':');
  return colon >= 0
    ? `${hash.slice(0, colon + 1)}${hash.slice(colon + 1, colon + 9)}…`
    : `${hash.slice(0, 12)}…`;
}
